import { useQueryClient } from '@tanstack/react-query';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { Webhook } from 'api';
import {
  getGetIntegrationWebhooksQueryKey,
  usePatchIntegrationWebhooksUuid,
  usePostIntegrationWebhooksBulkUpdate
} from 'api';

import { useOrganisationId } from 'hooks/context';

import { bulkOutcomeToast, summarizeBulkUpdate } from './bulkOutcome';
import type { ToggleMode } from './constants';
import { TOGGLE_WEBHOOK_ERROR } from './constants';
import { toggleWebhookSuccessMessage } from './utils';

export const useToggleWebhookStatus = () => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const patchMutation = usePatchIntegrationWebhooksUuid();
  const bulkMutation = usePostIntegrationWebhooksBulkUpdate();

  const toggle = (
    webhooks: Webhook[],
    mode: ToggleMode,
    onSuccess: () => void
  ) => {
    const active = mode === 'activate';
    const verb = active ? 'activated' : 'deactivated';

    const invalidate = () =>
      queryClient.invalidateQueries({
        queryKey: getGetIntegrationWebhooksQueryKey(organisationId)
      });

    const onError = () =>
      openNotification({ kind: 'error', message: TOGGLE_WEBHOOK_ERROR });

    if (webhooks.length === 1) {
      patchMutation.mutate(
        { organisationId, uuid: webhooks[0].uuid, data: { active } },
        {
          onSuccess: () => {
            invalidate();
            openNotification({
              kind: 'success',
              message: toggleWebhookSuccessMessage(mode, 1)
            });
            onSuccess();
          },
          onError
        }
      );
      return;
    }

    bulkMutation.mutate(
      {
        organisationId,
        data: {
          uuids: webhooks.map((webhook) => webhook.uuid),
          values: { active }
        }
      },
      {
        onSuccess: (response) => {
          invalidate();
          openNotification(
            bulkOutcomeToast(summarizeBulkUpdate(response), {
              allSuccess: toggleWebhookSuccessMessage(mode, webhooks.length),
              verb
            })
          );
          onSuccess();
        },
        onError
      }
    );
  };

  return {
    toggle,
    isPending: patchMutation.isPending || bulkMutation.isPending
  };
};
